import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LgvCompanyService } from '../../../core/services/lgv-company.service';
import { environment as env } from '../../../../environments/environment';
import { BehaviorSubject, first, map, mergeMap, Observable, tap } from 'rxjs';
import { LgvModel } from '../../../data-models/longevity.model';
import { LgvPricingModel } from '../models/pricing.model';
import { LgvClientService } from '../../../core/services/lgv-client.service';

@Injectable({
    providedIn: 'root'
})
export class LgvPricingService {
    constructor(
        private http: HttpClient,
        private lgvClientSvc: LgvClientService,
        private lgvCompanyService: LgvCompanyService
    ) {}

    //#region Pricing Scenarios
    public fetchScenario(
        scenarioID: string
    ): Observable<LgvPricingModel.PricingScenario> {
        return this.http.get<LgvPricingModel.PricingScenario>(
            `${env.LgvApiBaseUrl}/pricing/scenario/${scenarioID}`
        );
    }

    public addPricingScenario(
        model: any
    ): Observable<LgvPricingModel.PricingScenario> {
        let bldUrl = `${env.LgvApiBaseUrl}/pricing/scenario/`;
        return this.http.put<LgvPricingModel.PricingScenario>(bldUrl, model);
    }

    public deleteScenario(scenarioID: string): Observable<any> {
        return this.http.delete<any>(
            `${env.LgvApiBaseUrl}/pricing/scenario/${scenarioID}`
        );
    }

    public cancelLockRequest(scenarioId: string) {
        return this.http.post<any>(
            `${env.LgvApiBaseUrl}/pricing/scenario/${scenarioId}/lock-request/cancel`,
            {}
        );
    }
    //#endregion Pricing Scenarios

    public getCompanyPricingEngine(
        agentId: string
    ): Observable<LgvModel.ApplicationCompany> {
        const bldUrl = `${env.LgvApiBaseUrl}/users/${agentId}/snapshot`;

        return this.http.get<LgvModel.AgentSS>(bldUrl).pipe(
            mergeMap(userSS => {
                return this.getCompanyPricingEngineFromAgentSS(userSS);
            })
        );
    }

    public getCompanyPricingEngineFromAgentSS(
        agentSS: LgvModel.AgentSS
    ): Observable<LgvModel.ApplicationCompany> {
        const companyId = agentSS?.coBrandedAgent?.company?.id
            ? agentSS?.coBrandedAgent?.company?.id
            : agentSS?.agentInfo?.companyId;
        return this.lgvCompanyService.getById(companyId).pipe(
            map(company => {
                return company.applicationCompanies.find(
                    ac => ac.type === 'Pricing'
                );
            })
        );
    }

    public getCompanyPricingEnginePublic(
        companyCode: string
    ): Observable<LgvModel.ApplicationCompany> {
        return this.lgvCompanyService.getCompanyInfoByCode(companyCode).pipe(
            map(company => {
                return company.applicationCompanies.find(
                    ac => ac.type === 'Pricing'
                );
            })
        );
    }

    public todaysRatesProductSearchPublic(
        agentId: string,
        searchModel:
            | LgvPricingModel.ProductSearch
            | LgvPricingModel.ProductSearch
    ): Observable<LgvPricingModel.SearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/public/users/${agentId}/pricing/awr`;

        if (searchModel?.loanInformation?.calculateTotalLoanAmount) {
            delete searchModel?.loanInformation?.totalLoanAmountDetails;
        }

        return this.http
            .post<LgvPricingModel.SearchResult>(bldUrl, searchModel)
            .pipe(
                first(),
                tap(t1 => {
                    t1.products = t1.products.sort((a, b) =>
                        this.loanProductCompareOrder(a, b)
                    );
                })
            );
    }

    public productSearchClientOrAgent(
        clientID: string,
        agentID: string,
        searchModel: LgvPricingModel.ProductSearch
    ): Observable<LgvPricingModel.SearchResult> {
        if (clientID) {
            return this.productSearch(clientID, searchModel);
        } else {
            return this.productSearchPublicNoClient(agentID, searchModel);
        }
    }

    public productSearch(
        clientID: string,
        searchModel: LgvPricingModel.ProductSearch
    ): Observable<LgvPricingModel.SearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientID}/pricing/productsearch`;

        if (searchModel?.loanInformation?.calculateTotalLoanAmount) {
            delete searchModel?.loanInformation?.totalLoanAmountDetails;
        }

        return this.http
            .post<LgvPricingModel.SearchResult>(bldUrl, searchModel)
            .pipe(
                first(),
                tap(t1 => {
                    t1.products = t1.products.sort((a, b) =>
                        this.loanProductCompareOrder(a, b)
                    );
                })
            );
    }

    public productSearchPublicNoClient(
        agentId: string,
        searchModel:
            | LgvPricingModel.ProductSearch
            | LgvPricingModel.ProductSearch
    ): Observable<LgvPricingModel.SearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/public/users/${agentId}/pricing/productsearch`;

        if (searchModel?.loanInformation?.calculateTotalLoanAmount) {
            delete searchModel?.loanInformation?.totalLoanAmountDetails;
        }

        return this.http
            .post<LgvPricingModel.SearchResult>(bldUrl, searchModel)
            .pipe(
                first(),
                tap(t1 => {
                    t1.products = t1.products.sort((a, b) =>
                        this.loanProductCompareOrder(a, b)
                    );
                })
            );
    }

    public loanProductCompareOrder(
        a: LgvPricingModel.Product,
        b: LgvPricingModel.Product
    ): number {
        // Sort by Rate -> Price
        let result =
            (a.rate === b.rate ? 0 : a.rate < b.rate ? -1 : 1) ||
            (a.price == b.price ? 0 : a.price > b.price ? -1 : 1);

        return result;
    }

    public buildSearchModel(
        searchFormValue: any,
        adjustMinComp: boolean = true
    ): LgvPricingModel.ProductSearch {
        const searchModel = this.defaultOBProductSearch();
        const formModel = searchFormValue as LgvPricingModel.ProductSearch;

        // patch update the subset of fields in the form
        Object.keys(formModel).forEach(k1 => {
            Object.keys(formModel[k1]).forEach(k2 => {
                if (k2 in searchModel[k1]) {
                    searchModel[k1][k2] = formModel[k1][k2];
                }
            });
        });

        // don't include co-borrower object if there is not a non-occupant co-borrower
        if (
            searchModel.coBorrowerInformation &&
            searchModel.coBorrowerInformation.intentToOccupy == true
        ) {
            searchModel.coBorrowerInformation = null;
        }

        return searchModel;
    }

    public fetchQuotesForProduct(
        searchID: string,
        productID: string | number,
        clientID: string,
        agentID: string,
        loanAmount: number = null,
        loanTermInYears: string = null
    ): Observable<LgvPricingModel.ProductQuoteResult> {
        let bldUrl = '';

        if (clientID) {
            bldUrl = `${env.LgvApiBaseUrl}/clients/${clientID}/pricing/productsearch/${searchID}/products/${productID}`;
        } else {
            bldUrl = `${env.LgvApiBaseUrl}/public/users/${agentID}/pricing/productsearch/${searchID}/products/${productID}`;
        }

        if (loanAmount !== null && loanTermInYears !== null) {
            bldUrl += `?loanAmount=${loanAmount}&loanTermInYears=${loanTermInYears}`;
        }

        return this.http.get<LgvPricingModel.ProductQuoteResult>(bldUrl).pipe(
            first(),
            // insert a productID and quoteID into the quote
            tap(x => {
                for (let q of x.quotes) {
                    q.productId = productID;
                    q.id =
                        q.productId +
                        '_' +
                        q.lockPeriod.toString() +
                        '_' +
                        q.rate.toString();
                }
            })
        );
    }

    public buildProductSearchFromLoanOrScenario(
        loan: LgvModel.Loan,
        lScenario: LgvModel.LoanScenario,
        selectedProperty: LgvModel.Property,
        selectedClient: LgvModel.ClientSnapshot
    ): LgvPricingModel.ProductSearch | LgvPricingModel.ProductSearch {
        const is2nd = this.isSecondLoan(loan);
        const isRefi = !!loan && !!!lScenario;

        const propertyInfo = selectedProperty.propertyInfo;
        const model = this.defaultOBProductSearch();

        const ci = selectedClient.clientInfo;

        if (ci?.fico) {
            model.borrowerInformation.fico = ci.fico;
        }
        if (ci?.totalDebtPayments > 0 && ci?.totalIncome > 0) {
            model.borrowerInformation.debtToIncomeRatio = Math.round(
                (ci.totalDebtPayments / ci.totalIncome) * 100
            );
            model.loanInformation.loanLevelDebtToIncomeRatio =
                model.borrowerInformation.debtToIncomeRatio;
        }

        model.borrowerInformation.firstTimeHomeBuyer = ci.firstTimeBuyer;
        model.loanInformation.baseLoanAmount = Math.round(
            loan ? loan.loanAnalysis.estimatedBalance : lScenario?.loanAmount
        );
        model.propertyInformation.appraisedValue = propertyInfo.avmhighValue;
        model.propertyInformation.salesPrice = propertyInfo.avmhighValue;
        model.propertyInformation.county = propertyInfo.county;
        model.propertyInformation.state = propertyInfo.state;
        model.propertyInformation.zipCode = propertyInfo.zip;

        if (is2nd) {
            model.loanInformation.lienType = 'Second';
            model.loanInformation.loanPurpose = 'RefiCashout';
            const firstLoan = this.getFirstLoan(selectedProperty);
            if (firstLoan && firstLoan.loanAnalysis) {
                model.loanInformation.baseLoanAmount = Math.round(
                    firstLoan.loanAnalysis.estimatedBalance
                );
            }
        } else {
            model.loanInformation.lienType = 'First';
            model.loanInformation.loanPurpose = isRefi
                ? 'RefiRateTermLimitedCO'
                : 'Purchase';
        }

        model.loanInformation.loanType = '';

        if (loan) {
            switch (loan.loanType.name) {
                case 'CONV':
                    model.loanInformation.loanType = 'Conventional';
                    break;
                case 'HELOC':
                    model.loanInformation.loanType = 'HELOC';
                    break;
                case 'FHA':
                    model.loanInformation.loanType = 'FHA';
                    break;
                case 'VA':
                    model.loanInformation.loanType = 'VA';
                    break;
                case 'USDA/RHS':
                    model.loanInformation.loanType = 'USDARuralHousing';
                    break;
                default:
                    model.loanInformation.loanType = 'Conforming';
                    break;
            }
        } else {
            model.loanInformation.loanType = 'Conventional';

            switch (lScenario.residenceType) {
                case 'Primary':
                    model.propertyInformation.occupancy = 'PrimaryResidence';
                    break;
                case 'Secondary':
                    model.propertyInformation.occupancy = 'SecondHome';
                    break;
                case 'Investment':
                    model.propertyInformation.occupancy = 'InvestmentProperty';
                    break;
                default:
                    model.propertyInformation.occupancy = 'PrimaryResidence';
                    break;
            }
        }

        const county = selectedProperty.propertyInfo.county as string;
        if (county && county.trim().length > 0) {
            model.propertyInformation.county =
                selectedProperty.propertyInfo.county;
        }

        if (model.propertyInformation.zipCode) {
            model.propertyInformation.zipCode =
                selectedProperty.propertyInfo.zip;
        }

        return model;
    }

    public getFirstLoan(p: LgvModel.Property): LgvModel.Loan {
        const loan = this.lgvClientSvc.GetBestActiveLoan(p);
        return loan && loan.lienType && loan.lienType.name.includes('First')
            ? loan
            : null;
    }

    public isSecondLoan(loan: LgvModel.Loan): boolean {
        return (
            loan &&
            loan.lienType &&
            loan.lienType.name &&
            loan.lienType.name.includes('Second')
        );
    }

    public cashoutRefiSearchModel(
        isLP: boolean,
        clientID: string,
        baseLoanAmount: number,
        homeValue: number,
        cashOutAmount: number,
        county: string,
        state: string,
        zipCode: string
    ): LgvPricingModel.ProductSearch {
        const searchModel = this.defaultOBProductSearch();
        searchModel.loanInformation.loanPurpose = 'RefiCashout';
        searchModel.loanInformation.loanTerms = ['ThirtyYear'];
        searchModel.loanInformation.baseLoanAmount = baseLoanAmount;
        searchModel.loanInformation.cashOutAmount = cashOutAmount;
        searchModel.propertyInformation.appraisedValue = homeValue;
        searchModel.propertyInformation.salesPrice = homeValue;
        searchModel.propertyInformation.county = county;
        searchModel.propertyInformation.state = state;
        searchModel.propertyInformation.zipCode = zipCode;
        return searchModel;
    }

    public secondLienSearchModel(
        isLP: boolean,
        clientID: string,
        baseLoanAmount: number,
        firstLienAmount: number,
        homeValue: number,
        interestOnly: boolean,
        county: string,
        state: string,
        zipCode: string
    ): LgvPricingModel.ProductSearch {
        const searchModel = this.defaultOBProductSearch();
        searchModel.loanInformation.loanPurpose = 'RefiCashout';
        searchModel.loanInformation.automatedUnderwritingSystem =
            'NotSpecified';
        searchModel.loanInformation.lienType = 'Second';
        searchModel.loanInformation.baseLoanAmount = baseLoanAmount;
        searchModel.loanInformation.firstLienAmount = firstLienAmount;
        searchModel.propertyInformation.appraisedValue = homeValue;
        searchModel.propertyInformation.salesPrice = homeValue;
        searchModel.loanInformation.interestOnly = interestOnly;
        searchModel.propertyInformation.county = county;
        searchModel.propertyInformation.state = state;
        searchModel.propertyInformation.zipCode = zipCode;
        return searchModel;
    }

    defaultOBProductSearch(): LgvPricingModel.ProductSearch {
        let model: any = this.defaultProductSearchJson;

        model.borrowerInformation.monthsReserves = 12;
        model.propertyInformation.numberOfUnits = 'OneUnit';
        model.customerInternalId = 'OBSearch';

        model.loanInformation.loanPurpose = 'Purchase';
        model.loanInformation.loanType = 'Conventional';

        return model as LgvPricingModel.ProductSearch;
    }

    get defaultProductSearchJson(): LgvPricingModel.ProductSearch {
        return {
            qmInformation: {
                refinancePrepaymentFeeAmount: 0,
                creditInsuranceFeeAmount: 0,
                cashIncentiveDiscountAmount: 0,
                escrowTrusteeFeeAmount: 0,
                propertyAssessmentFeeAmount: 0,
                notaryCreditReportFeeAmount: 0,
                documentPreperationFeeAmount: 0,
                titleFeeAmount: 0,
                borrowerPaidLOCompensation: 0,
                additionalQMFeesAffectAPOR: false,
                additionalQMFeeAmount: 0,
                financedPMI: 0
            },
            borrowerInformation: {
                citizenship: 'USCitizen',
                debtToIncomeRatio: 30,
                fico: 780,
                firstName: 'test',
                lastName: 'test1',
                vaFirstTimeUse: false,
                firstTimeHomeBuyer: false,
                monthsReserves: 0,
                selfEmployed: false,
                waiveEscrows: false,
                state: null,
                typeOfVeteran: null
            },
            coBorrowerInformation: {
                intentToOccupy: true
            },
            propertyInformation: {
                appraisedValue: 280000.0,
                occupancy: 'PrimaryResidence',
                propertyStreetAddress: null,
                city: null,
                county: null,
                state: 'CA',
                zipCode: null,
                propertyType: 'SingleFamily',
                corporateRelocation: false,
                salesPrice: 280000.0,
                numberOfStories: 1,
                numberOfUnits: null
            },
            loanInformation: {
                loanPurpose: 'Purchase',
                lienType: 'First',
                amortizationTypes: ['Fixed'],
                armFixedTerms: ['FiveYear', 'TenYear'],
                productTypes: ['StandardProducts'],
                propertiesFinanced: null,
                automatedUnderwritingSystem: 'NotSpecified',
                borrowerPaidMI: 'Yes',
                buydown: 'None',
                cashOutAmount: 0,
                desiredLockPeriod: 30,
                desiredPrice: 0,
                desiredRate: 0,
                feesIn: 'No',
                expandedApprovalLevel: 'NotApplicable',
                interestOnly: false,
                baseLoanAmount: 210000,
                firstLienAmount: 0,
                secondLienAmount: 0,
                helocDrawnAmount: 0,
                helocLineAmount: 0,
                loanTerms: ['ThirtyYear', 'FifteenYear'],
                loanType: 'Conforming',
                prepaymentPenalty: 'None',
                exemptFromVAFundingFee: false,
                includeLOCompensationInPricing: 'YesLenderPaid',
                calculateTotalLoanAmount: true,
                assetDepletion: 'No',
                autoDebit: 'No',
                employeeLoan: 'No',
                communityAffordableSecond: 'No',
                reducedMI: false,
                representativeFICO: 780,
                loanLevelDebtToIncomeRatio: 30.0,
                totalMonthlyQualifyingIncome: 0.0,
                customerInternalId: 'OBSearch',
                downPaymentPercent: 0,
                expandedGuidelines: {
                    incomeVerificationType: 'FullDoc',
                    housingEventType: 'None',
                    housingEventSeasoning: 'NotApplicable',
                    bankruptcyType: 'None',
                    bankruptcyOutcome: 'NotApplicable',
                    bankruptcySeasoning: 'NotApplicable',
                    mortgageLatesx30_12Mos: 0,
                    mortgageLatesx30_13to24Mos: 0,
                    mortgageLatesx60_12Mos: 0,
                    mortgageLatesx60_13to24Mos: 0,
                    mortgageLatesx90_12Mos: 0,
                    mortgageLatesx90_13to24Mos: 0,
                    mortgageLatesx120_12Mos: 0,
                    mortgageLatesx120_13to24Mos: 0,
                    debtConsolidation: false,
                    uniqueProperty: false
                },
                totalLoanAmountDetails: {
                    totalLoanAmount: 210000,
                    upfrontPmiMipFfGfPercent: 0,
                    upfrontPmiMipFfGfAmount: 0,
                    upfrontPmiMipFfGfPaidinCash: 0,
                    upfrontPmiMipFfGfFinancedAmount: 0
                }
            },
            version: '1.0',
            agentId: undefined
        };
    }

    public ineligibleProductSearch(
        clientID: string,
        productSearchId: string
    ): Observable<LgvPricingModel.IneligibleSearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/clients/${clientID}/pricing/productsearch/${productSearchId}/products/ineligible`;

        return this.http
            .get<LgvPricingModel.IneligibleSearchResult>(bldUrl)
            .pipe(first());
    }

    public ineligibleProductSearchByAgent(
        agentID: string,
        productSearchId: string
    ): Observable<LgvPricingModel.IneligibleSearchResult> {
        let bldUrl = `${env.LgvApiBaseUrl}/users/${agentID}/pricing/productsearch/${productSearchId}/products/ineligible`;

        return this.http
            .get<LgvPricingModel.IneligibleSearchResult>(bldUrl)
            .pipe(first());
    }

    // Find the best rate equal to or above target price (For Product OR Quote - AKA RateAndPriceObj)
    static findBestRateInArray(
        objects: LgvPricingModel.RateAndPriceObj[],
        targetPrice: number
    ): LgvPricingModel.RateAndPriceObj {
        let sortedObjects = objects.sort((a, b) =>
            this.productCompareOrder(a, b)
        );
        let resultsInTargetRange = sortedObjects.filter(
            x => x.price >= targetPrice
        );

        // if results in target range, return the best rate (1st item)
        if (resultsInTargetRange.length > 0) {
            return resultsInTargetRange[0];
        }

        // all products are below target price, so find the highest price (new target price), and find the best rate at that price
        let sortedByPrice = objects.sort((a, b) =>
            a.price > b.price ? -1 : 1
        );
        let adjTargetPrice = sortedByPrice[0].price;
        let resultsInAdjustedTargetRange = sortedObjects.filter(
            x => x.price >= adjTargetPrice
        );

        // should be at least one...
        return resultsInAdjustedTargetRange[0];
    }

    // Compare products to find the best match based on rate, APR, and price
    static productCompareOrder(
        a: LgvPricingModel.RateAndPriceObj,
        b: LgvPricingModel.RateAndPriceObj
    ): number {
        return (
            (a.rate < b.rate ? -1 : 1) ||
            (a.apr < b.apr ? -1 : 1) ||
            (a.price >= b.apr ? -1 : 1)
        );
    }

    // pricing lookup
    public fetchPricingStatuses(): Observable<LgvPricingModel.PricingStatus[]> {
        const bldUrl = `${env.LgvApiBaseUrl}/types/pricing-scenario-status`;
        return this.http
            .get<LgvPricingModel.PricingStatus[]>(bldUrl)
            .pipe(first());
    }

    public fetchScenariosByLoanAppId(
        loanAppId: string
    ): Observable<LgvPricingModel.PricingScenario[]> {
        const bldUrl = `${env.LgvApiBaseUrl}/pricing/scenario/loanapplications/${loanAppId}`;
        return this.http
            .get<LgvPricingModel.PricingScenario[]>(bldUrl)
            .pipe(first());
    }

    public fetchCurrentPreApprovedPricingfromScenarios(loanAppId: string) {
        const bldUrl = `${env.LgvApiBaseUrl}/pricing/scenario/loanapplications/${loanAppId}`;
        return this.http.get<LgvPricingModel.PricingScenario[]>(bldUrl).pipe(
            first(),
            map(scenarios => {
                return this.getCurrentPreApprovedPricing(scenarios);
            })
        );
    }

    public getCurrentPreApprovedPricing(
        scenarios: LgvPricingModel.PricingScenario[]
    ) {
        return scenarios?.find(s => this.checkIfCurrentPreApprovedPricing(s));
    }

    public getDealDeskPreApprovedPricing(
        scenarios: LgvPricingModel.PricingScenario[]
    ) {
        return scenarios?.find(s => this.checkIfDealDeskPreApprovedPricing(s));
    }

    checkIfCurrentPreApprovedPricing(
        scenario: LgvPricingModel.PricingScenario
    ) {
        return !!scenario?.dealDeskResponse && !!scenario?.dealDeskRequest;
    }

    checkIfDealDeskPreApprovedPricing(
        scenario: LgvPricingModel.PricingScenario
    ) {
        return (
            !!scenario?.dealDeskResponse &&
            !!scenario?.dealDeskRequest &&
            !scenario?.response &&
            !scenario?.request
        );
    }

    public isLockRequestAllowed() {
        const currentHours = new Date().getUTCHours();
        const currentMinutes = new Date().getUTCMinutes();

        // 9:30AM EST is 1:30PM UTC (13:30)
        // 7PM EST is 11PM UTC (23:00)

        return (
            (currentHours === 13 && currentMinutes >= 30) ||
            (currentHours > 13 && currentHours <= 23)
        );
    }

    public notifyFoLockRequest() {
        //TODO: call api once available
        console.log('notify for lock request');
    }
}
